import { AnalyticsBrowser } from '@segment/analytics-next';
import {
	debounce,
	generateUUID,
	getCookie,
	getCookies,
	getCookieDomain,
	isUserAgentAutomated,
} from '../utility/index.js';

// ============================================================================
// SHARED CONSTANTS AND UTILITIES
// ============================================================================

// Segment Events Constants
const SegmentEvents = {
	FORM_SUBMITTED: 'Form Submitted',
	USER_CLICKED: 'User Clicked',
	USER_INPUTTED: 'User Inputted',
};

// Tracking Configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

/**
 * Get base tracking data with common properties
 * @return {Object} Base tracking data object
 */
export const getBaseTrackingData = () => ({
	isAutomatedTest: false,
	isAWS: false,
	isBot: false,
	isCypress: false,
	isDatadog: false,
	isMobileDevice: !!window._GROWTHERAPY_IS_MOBILE,
});

// ============================================================================
// SEGMENT ANALYTICS CORE CLASS
// ============================================================================

/**
 * Segment Analytics Core
 * Handles initialization, page tracking, and cookie management
 */
export default class SegmentAnalytics {
	constructor() {
		if (isUserAgentAutomated()) {
			return;
		}

		const analyticsConfig = window.analyticsConfig || {};
		const segmentConfig = analyticsConfig.segment || {};

		this.config = {
			cookieRefreshThreshold: 30, // days
			mobileBreakpoint: 600,
			productionKey: segmentConfig.writeKey,
			stagingKey: segmentConfig.writeKey,
		};

		this.mobileResizeHandler();

		this.environment = analyticsConfig.environment;
		this.date = new Date();
		this.date.setFullYear(this.date.getFullYear() + 1);
		this.expirationDateStringForCookies = `expires=${this.date.toUTCString()}`;
		this.isAmazonLP = window.location.href.includes('/benefits/amazon');

		this.initialize();
	}

	/**
	 * Setup mobile detection with debounced resize handler
	 *
	 * @return {void}
	 */
	mobileResizeHandler() {
		const checkIfMobile = () => {
			window._GROWTHERAPY_IS_MOBILE =
				window.innerWidth < this.config.mobileBreakpoint;
		};

		checkIfMobile();
		const resizeHandler = debounce(checkIfMobile, 100);
		window.addEventListener('resize', resizeHandler);
	}

	/**
	 * Initialize Segment Analytics
	 *
	 * @return {void}
	 */
	initialize() {
		try {
			const writeKey = this.getWriteKey();
			if (!writeKey) {
				// eslint-disable-next-line no-console
				console.warn('Segment write key not configured');
				return;
			}

			this.loadSegmentScript(writeKey);
			this.getPageViewCookies();
			this.trackPageView();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Segment initialization error:', error);
		}
	}

	/**
	 * Get write key based on environment
	 *
	 * @return {string} Write key
	 */
	getWriteKey() {
		return this.environment === 'production'
			? this.config.productionKey
			: this.config.stagingKey;
	}

	/**
	 * Load Segment Analytics script
	 *
	 * @param {string} writeKey - Segment write key
	 * @return {void}
	 */
	loadSegmentScript(writeKey) {
		if (window.analytics) {
			// eslint-disable-next-line no-console
			console.warn('Segment Analytics already loaded');
			return;
		}

		window.analytics = AnalyticsBrowser.load({ writeKey });
	}

	/**
	 * Gets required cookies for page view tracking
	 * The statsigStableIdCookie is the same as the growUniversalIdCookie
	 *
	 * @return {void}
	 */
	getPageViewCookies() {
		const cookies = getCookies(['_vwo_uuid_v2', 'grow_universal_id']);

		this.growUniversalIdCookie = this.getGrowUniversalId(
			cookies.grow_universal_id,
		);
		this.statsigStableIdCookie = this.growUniversalIdCookie;
		this.vwoUuidCookie = cookies._vwo_uuid_v2;
	}

	/**
	 * Business requirement: If the growUniversalId parameter is present in the URL, use that as the source of truth for the ID
	 * Otherwise, use the ID passed in as a parameter
	 *
	 * @param {string} id - ID to use if the growUniversalId parameter is not present in the URL
	 * @return {string} The ID to use
	 */
	getGrowUniversalId(id) {
		const urlParams = new URLSearchParams(window.location.search);
		const growUniversalIdParam = urlParams.get('growUniversalId');
		const shouldUseGrowUniversalIdParam =
			growUniversalIdParam && growUniversalIdParam !== '';

		return shouldUseGrowUniversalIdParam ? growUniversalIdParam : id;
	}

	/**
	 * Tracks page views for new and returning users
	 *
	 * @return {void}
	 */
	trackPageView() {
		if (!this.growUniversalIdCookie) {
			this.handleNewUserPageView();
		} else {
			this.handleReturningUserPageView();
		}
	}

	/**
	 * Handles page view tracking for new users
	 * If there is an anonymous ID from segment, use that, otherwise generate a new one
	 *
	 * @return {void}
	 */
	handleNewUserPageView() {
		const anonymousId = generateUUID();

		const pageProperties = this.buildPageProperties(
			this.statsigStableIdCookie,
			anonymousId,
			this.vwoUuidCookie,
		);

		this.handlePageViewEvent(pageProperties, anonymousId);
	}

	/**
	 * Handles page view tracking for returning users
	 *
	 * @return {void}
	 */
	handleReturningUserPageView() {
		const pageProperties = this.buildPageProperties(
			this.statsigStableIdCookie,
			this.growUniversalIdCookie,
			this.vwoUuidCookie,
		);

		this.handlePageViewEvent(pageProperties, this.growUniversalIdCookie);
		this.refreshCookieIfNeeded(this.growUniversalIdCookie);
	}

	/**
	 * Build page properties object
	 * @param {string} statsigStableIdCookie - Statsig stable ID
	 * @param {string} segmentCookie         - Segment cookie
	 * @param {string} vwoUuid               - VWO UUID
	 * @return {Object} Page properties
	 */
	buildPageProperties(statsigStableIdCookie, segmentCookie, vwoUuid) {
		const properties = {
			statsigCustomIDs: [
				'stableID',
				statsigStableIdCookie,
				'growUniversalId',
				segmentCookie,
			],
			statsigEnvironment: {
				tier: this.environment,
			},
			growUniversalId: segmentCookie,
			vwoUuid,
			isAutomatedTest: false,
			isCypress: false,
			isDatadog: false,
			isAWS: false,
			isBot: false,
			isMobileDevice: !!window._GROWTHERAPY_IS_MOBILE,
		};

		if (this.isAmazonLP) {
			properties.type = 'Amazon EAP';
		}

		return properties;
	}

	/**
	 * Handle page view events to segment
	 *
	 * @param {Object} pageProperties - Page properties
	 * @param {string} anonymousId    - Anonymous ID
	 * @return {void}
	 */
	handlePageViewEvent(pageProperties, anonymousId) {
		const eventType = this.getPageViewEventType();

		window.analytics.page(undefined, eventType, pageProperties, {
			anonymousId,
		});

		this.setCookie('grow_universal_id', anonymousId);
		this.setCookie('grow_universal_id_expiration', this.getFormattedDate());
		this.setCookie('statsig_stable_id', anonymousId);
	}

	/**
	 * Set cookie with optimized parameters
	 *
	 * @param {string} name  - Cookie name
	 * @param {string} value - Cookie value
	 */
	setCookie(name, value) {
		document.cookie = `${name}=${value}; Secure; ${this.expirationDateStringForCookies}; domain=${getCookieDomain()}; path=/`;
	}

	/**
	 * Get event type based on pathname
	 *
	 * @return {string} Event type
	 */
	getPageViewEventType() {
		const multiStepForm = document.querySelector(
			'[data-multi-step-search-filter-form]',
		);
		if (multiStepForm) {
			const isLongForm =
				multiStepForm.getAttribute('data-multi-step-search-filter-form') ===
				'long';
			return isLongForm ? 'Long Quiz Viewed' : 'Short Quiz Viewed';
		}

		const { pathname } = window.location;
		if (pathname === '/start/find-therapist-social/') {
			return 'Social LP Viewed';
		}

		if (pathname === '/') {
			return 'organic site - homepage';
		}

		if (pathname.startsWith('/start/')) {
			return 'organic site - landing page';
		}

		return 'organic site - other page';
	}

	/**
	 * Format date to YYYY-MM-DD
	 *
	 * @return {string} Formatted date
	 */
	getFormattedDate() {
		return this.date.toISOString().split('T')[0];
	}

	/**
	 * Refresh cookie if needed
	 *
	 * @param {string} segmentCookie - Segment cookie value
	 * @return {void}
	 */
	refreshCookieIfNeeded(segmentCookie) {
		const cookieExpirationDate = getCookie('grow_universal_id_expiration');

		if (cookieExpirationDate) {
			const now = Date.now();
			const expirationTime = new Date(cookieExpirationDate).getTime();
			const daysUntilExpiry = Math.floor(
				(expirationTime - now) / (1000 * 60 * 60 * 24),
			);

			if (daysUntilExpiry < this.config.cookieRefreshThreshold) {
				this.setCookie('grow_universal_id', segmentCookie);
				this.setCookie('grow_universal_id_expiration', this.getFormattedDate());
				this.setCookie('statsig_stable_id', segmentCookie);
			}
		}
	}
}

// ============================================================================
// SEGMENT TRACKING UTILITIES CLASS
// ============================================================================

/**
 * Segment Tracking Utilities
 * Handles event tracking for buttons, links, and user inputs
 */
export class SegmentTracking {
	constructor() {
		this.retryConfig = { maxRetries: MAX_RETRIES, retryDelay: RETRY_DELAY };
	}

	/**
	 * Send tracking event with retry logic
	 * @param {string} name - Event name
	 * @param {Object} data - Event data
	 * @return {Promise<string>} Promise that resolves to 'success'
	 */
	sendTrackingEvent(name, data) {
		let retries = 0;

		return new Promise((resolve, reject) => {
			const waitForAnalytics = () => {
				if (window.analytics) {
					// Coerce the shared getCookie's null (unset) back to undefined
					// so absent ids drop out of the serialized payload as before.
					const vwoUuid = getCookie('_vwo_uuid_v2') ?? undefined;
					const growUniversalId = getCookie('grow_universal_id') ?? undefined;
					const trackingData = {
						...data,
						growUniversalId,
						vwoUuid,
					};

					window.analytics.track(name, trackingData, {}, () =>
						resolve('success'),
					);
				} else if (retries < this.retryConfig.maxRetries) {
					retries += 1;
					setTimeout(waitForAnalytics, this.retryConfig.retryDelay);
				} else {
					reject('Analytics object not present on window.');
				}
			};

			waitForAnalytics();
		});
	}

	/**
	 * Setup anchor link click tracking
	 * @param {HTMLCollection<HTMLAnchorElement>} elements     - Anchor elements to track
	 * @param {string}                            name         - Event name
	 * @param {Function}                          getElementId - Function to get element ID
	 * @param {boolean}                           modifyUrl    - Whether to modify URLs
	 */
	setupAnchorLinkClickTracking(
		elements,
		name,
		getElementId,
		modifyUrl = false,
	) {
		const windowPathname = window.location.pathname;
		const hostname = modifyUrl ? new URL(window.location.href).hostname : null;

		Array.from(elements).forEach((element) => {
			try {
				const elementId = getElementId(element);
				if (!elementId) {
					return;
				}

				if (modifyUrl) {
					const url =
						hostname === 'growtherapy.com'
							? element.href
							: element.href.replace(
									'growtherapy.com',
									'growtherapystaging.com',
								);
					element.href = url;
				}

				const linkHrefPathname = new URL(element.href).pathname;
				const destinationUrl =
					linkHrefPathname !== windowPathname ? element.href : undefined;

				const trackingData = {
					elementId,
					...(destinationUrl && { destinationUrl }),
					...getBaseTrackingData(),
				};

				element.addEventListener('click', () => {
					this.sendTrackingEvent(name, trackingData).catch((error) => {
						// eslint-disable-next-line no-console
						console.error(`Failed to track anchor link click: ${error}`);
					});
				});
			} catch (error) {
				if (error instanceof Error) {
					// eslint-disable-next-line no-console
					console.error(
						`Failed to setup tracking for element: ${error.message}`,
					);
				}
			}
		});
	}

	/**
	 * Track button click event
	 * @param {string}           eventType  - Type of button click event
	 * @param {object|undefined} properties - Additional properties to track
	 */
	trackButtonClick(eventType, properties = undefined) {
		try {
			const trackingData = {
				type: eventType,
				...properties,
				...getBaseTrackingData(),
			};

			this.sendTrackingEvent(SegmentEvents.USER_CLICKED, trackingData).catch(
				(error) => {
					// eslint-disable-next-line no-console
					console.error(`Failed to track button click: ${error}`);
				},
			);
		} catch (error) {
			if (error instanceof Error) {
				// eslint-disable-next-line no-console
				console.error(`Failed to setup tracking for element: ${error.message}`);
			}
		}
	}

	trackFormSubmit(name, properties = undefined) {
		try {
			const trackingData = {
				type: name,
				...properties,
				...getBaseTrackingData(),
			};

			this.sendTrackingEvent(SegmentEvents.FORM_SUBMITTED, trackingData).catch(
				(error) => {
					// eslint-disable-next-line no-console
					console.error(`Failed to track form submit: ${error}`);
				},
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.debug(error);
			if (error instanceof Error) {
				// eslint-disable-next-line no-console
				console.error(`Failed to setup tracking for element: ${error.message}`);
			}
		}
	}

	/**
	 * Track user input event
	 * @param {string} name       - Input event name
	 * @param {Object} properties - Input properties
	 */
	trackUserInput(name, properties) {
		try {
			const trackingData = {
				type: name,
				...properties,
				...getBaseTrackingData(),
			};

			this.sendTrackingEvent(SegmentEvents.USER_INPUTTED, trackingData).catch(
				(error) => {
					// eslint-disable-next-line no-console
					console.error(`Failed to track input: ${error}`);
				},
			);
		} catch (error) {
			if (error instanceof Error) {
				// eslint-disable-next-line no-console
				console.error(`Failed to setup tracking for element: ${error.message}`);
			}
		}
	}
}

// ============================================================================
// INITIALIZATION AND GLOBAL EXPORTS
// ============================================================================

// Create global tracking instance and export functions
const trackingInstance = new SegmentTracking();

// Export tracking functions for use in other scripts
window.SegmentTracking = {
	setupAnchorLinkClickTracking:
		trackingInstance.setupAnchorLinkClickTracking.bind(trackingInstance),
	sendTrackingEvent: trackingInstance.sendTrackingEvent.bind(trackingInstance),
	trackButtonClick: trackingInstance.trackButtonClick.bind(trackingInstance),
	trackFormSubmit: trackingInstance.trackFormSubmit.bind(trackingInstance),
	trackUserInput: trackingInstance.trackUserInput.bind(trackingInstance),
};
